import { useEffect, useState } from 'react'
import { EmptyState, Skeleton } from '@indipia/ui-core'
import { apiClient, useAuthStore } from '@indipia/shared'
import { useModalA11y } from '../hooks/useModalA11y'
import { Z_POPUP } from '../constants/zIndex'
import { ErrorBanner } from './AsyncState'
import { ThumbImg } from './ThumbImg'

interface FieldReportPhoto {
  frPhotoId: number
  thumbUrl:  string
}

interface FieldReportItem {
  fieldReportId:  number
  writerName:     string | null
  reportContent:  string
  photos:         FieldReportPhoto[]
  reportDtm:      string
}

const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토']

function fmtDateHeader(dtm: string): string {
  const d = new Date(dtm)
  if (Number.isNaN(d.getTime())) return dtm
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} (${WEEKDAY[d.getDay()]})`
}

function fmtTime(dtm: string): string {
  const d = new Date(dtm)
  if (Number.isNaN(d.getTime())) return ''
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/**
 * 공사현황보기 팝업 (C-1-11) — 간트/캘린더/리스트(스케줄 관리) 3개 화면과
 * 현장 관리 > 현장 목록에서 공용으로 쓴다. 부모가 조건부 렌더링으로 마운트하는
 * 것을 전제로 useModalA11y(true, onClose)를 내부에서 직접 호출한다.
 */
export function FieldReportStatusPopup({
  onClose, siteId, siteName,
}: {
  onClose:  () => void
  siteId?:  number
  siteName?: string
}) {
  const user  = useAuthStore(s => s.user)
  const modal = useModalA11y(true, onClose)

  const [items,     setItems]     = useState<FieldReportItem[]>([])
  const [loading,   setLoading]   = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!siteId) { setLoading(false); return }
    setLoading(true)
    setLoadError(false)
    const headers = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
    apiClient
      .get<{ data: { items: FieldReportItem[] } }>('/schedule/field-reports', { params: { siteId }, headers })
      .then(res => setItems(res.data.data?.items ?? []))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }, [siteId, user?.compId, reloadKey])

  // 최신순(reportDtm desc)으로 내려오는 목록을 날짜별로 묶는다.
  const groups: { dateKey: string; label: string; items: FieldReportItem[] }[] = []
  for (const it of items) {
    const dateKey = it.reportDtm?.slice(0, 10) ?? ''
    const last = groups[groups.length - 1]
    if (!last || last.dateKey !== dateKey) {
      groups.push({ dateKey, label: fmtDateHeader(it.reportDtm), items: [it] })
    } else {
      last.items.push(it)
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'var(--alpha-black-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: Z_POPUP, overflow: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={modal.titleId}
        tabIndex={-1}
        ref={modal.boxRef}
        style={{ background: 'var(--shell-surface)', borderRadius: 16, boxShadow: 'var(--elevation-3)', width: '100%', maxWidth: 660, overflow: 'hidden', maxHeight: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }}
      >
        {/* 헤더 */}
        <div style={{ background: 'var(--shell-bg)', padding: '16px 20px', borderBottom: '1px solid var(--shell-border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2.2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            <span id={modal.titleId} style={{ fontSize: 15, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-.4px', flex: 1 }}>공사현황보기</span>
            {siteName && <span style={{ fontSize: 12, color: 'var(--shell-on-surface-2)' }}>{siteName}</span>}
            {!loading && !loadError && (
              <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--gt-primary)', background: 'var(--gt-subtle)', padding: '2px 9px', borderRadius: 12 }}>보고 {items.length}건</span>
            )}
            <button onClick={onClose} aria-label="닫기" style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'var(--shell-border)', color: 'var(--shell-on-surface)', cursor: 'pointer', fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', lineHeight: 1 }}>×</button>
          </div>
        </div>

        {/* 보고 목록 */}
        <div style={{ overflowY: 'auto', padding: '16px 20px', flex: 1 }} aria-busy={loading}>
          {loading && (
            <div aria-busy="true" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 24 }}>{[0, 1, 2].map(i => <Skeleton key={i} height={28} />)}</div>
          )}
          {!loading && loadError && (
            <ErrorBanner message="현장보고를 불러오지 못했습니다." onRetry={() => setReloadKey(k => k + 1)} />
          )}
          {!loading && !loadError && items.length === 0 && (
            <EmptyState title="등록된 현장보고가 없습니다" />
          )}
          {!loading && !loadError && groups.map(g => (
            <div key={g.dateKey}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid var(--shell-border)' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)' }}>{g.label}</span>
              </div>
              {g.items.map(it => (
                <div key={it.fieldReportId} style={{ background: 'var(--shell-surface)', borderRadius: 12, border: '1px solid var(--shell-border)', overflow: 'hidden', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px 10px' }}>
                    <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-surface)', background: 'var(--color-success)', padding: '3px 9px', borderRadius: 24, flexShrink: 0 }}>현장보고</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--shell-on-surface)' }}>{it.writerName || '—'}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--shell-on-surface-2)', flexShrink: 0 }}>{fmtTime(it.reportDtm)}</span>
                  </div>
                  <div style={{ padding: '0 16px 12px', fontSize: 13, color: 'var(--shell-on-surface)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{it.reportContent}</div>
                  {it.photos.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, padding: '0 16px 14px', overflowX: 'auto' }}>
                      {it.photos.map(p => (
                        <ThumbImg key={p.frPhotoId} url={p.thumbUrl} className="h-16 w-16 shrink-0 rounded-xl border-2 border-border" />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
